import * as stylex from '@stylexjs/stylex';
import { useMatches, useRouteContext, useRouter, useSearch } from '@tanstack/react-router';
import { useState, type ReactElement, type ReactNode } from 'react';
import {
  MenuTrigger,
  useDragAndDrop,
  useListData,
  Button as ButtonCore,
  Checkbox as CheckboxCore,
} from 'react-aria-components';

import {
  Button,
  DIALOG_SLOTS,
  DialogBar,
  EyeStrokeIcon,
  GridList,
  GridListItem,
  Heading,
  IconButton,
  ReorderVerticalLinesStrokeIcon,
  Text,
  type TableColumnConfig,
  useLocalStorage,
  EyeOffStrokeIcon,
  Divider,
  CopyStrokeIcon,
  Dialog,
  type IconButtonProps,
  LinkStrokeIcon,
  Menu,
  MenuItem,
  MoreVerticalStrokeIcon,
  Popover,
  SettingsStrokeIcon,
  TOAST_TIMEOUT,
  useCopyToClipboard,
} from '@/libs/serenity-ui';
import { $ } from '@/libs/serenity-ui/tokens.stylex';
import type { RouterContext } from '@/routes/__root';
import type { ListRouteContext } from '@/routes/_main/_list';

interface SecondaryActionsIconButtonProps extends Omit<IconButtonProps, 'children' | 'id'> {
  id?: string | number;
  children?: ReactNode;
  isTableActions?: boolean;
  hash?: string;
  resource?: string;
}

export function SecondaryActionsIconButton({ id, children, isTableActions = false, hash, resource, ...props }: SecondaryActionsIconButtonProps): ReactElement {
  const [ isCustomizeDialogOpen, setIsCustomizeDialogOpen ] = useState(false);

  const { toastQueue } = useRouteContext({ from: '/_main' });
  const [ , copyToClipboard ] = useCopyToClipboard();
  const router = useRouter();

  const search = useSearch({ from: '/_main/_list', shouldThrow: false });
  const matches = useMatches();
  const listRouteContext = matches.at(-1)?.context as RouterContext & ListRouteContext;

  const resolvedHash = hash ?? (isTableActions ? 'table' : 'details');
  const resolvedResource = resource ?? listRouteContext?.resource;
  const showCustomize = isTableActions || !!resource;

  const copyLinkPath = `${window.location.origin}${router.buildLocation({
    to: '.',
    ...(isTableActions && { search }),
    hash: resolvedHash,
  }).href}`;

  const handleCopyLink = (): void => {
    void copyToClipboard(copyLinkPath).then(() =>
      toastQueue.add({ message: 'Copied to clipboard', variant: 'success' }, { timeout: TOAST_TIMEOUT }));
  };

  const handleCopyId = (): void => {
    if (!id) {
      return;
    }
    void copyToClipboard(String(id)).then(() =>
      toastQueue.add({ message: 'Copied to clipboard', variant: 'success' }, { timeout: TOAST_TIMEOUT }));
  };

  return (
    <>
      <MenuTrigger>
        <IconButton aria-label="More actions" variant="ghost" {...props}>
          <MoreVerticalStrokeIcon />
        </IconButton>

        <Popover>
          <Menu>
            <MenuItem onAction={handleCopyLink}>
              <LinkStrokeIcon />
              <Text>Copy link</Text>
            </MenuItem>

            {!isTableActions && id && (
              <MenuItem onAction={handleCopyId}>
                <CopyStrokeIcon />
                <Text>Copy ID</Text>
              </MenuItem>
            )}

            {children}

            {showCustomize && (
              <MenuItem onAction={() => setIsCustomizeDialogOpen(true)}>
                <SettingsStrokeIcon />
                <Text>Customize</Text>
              </MenuItem>
            )}
          </Menu>
        </Popover>
      </MenuTrigger>

      {showCustomize && (
        <Dialog
          isOpen={isCustomizeDialogOpen}
          onOpenChange={setIsCustomizeDialogOpen}>
          <CustomizeTableDialogContent resource={resolvedResource} onClose={() => setIsCustomizeDialogOpen(false)} />
        </Dialog>
      )}
    </>
  );
}

interface CustomizeTableDialogContent {
  resource: string;
  onClose: () => void;
}

export function CustomizeTableDialogContent({ resource, onClose }: CustomizeTableDialogContent) {
  const [ columnsConfig, setColumnsConfig ] = useLocalStorage<TableColumnConfig[]>(`table-columns-${resource}`, []);

  const list = useListData({
    initialItems: columnsConfig,
    getKey: (item) => item.id,
  });

  const { dragAndDropHooks } = useDragAndDrop({
    getItems: (_, items: typeof list.items) => items.map((item) => ({ 'text/plain': item.label })),
    onReorder(e) {
      if (e.target.dropPosition === 'before') {
        list.moveBefore(e.target.key, e.keys);
      } else if (e.target.dropPosition === 'after') {
        list.moveAfter(e.target.key, e.keys);
      }
    },
    renderDropIndicator: () => (
      <div {...stylex.props(dropIndicatorStyles.base)}>
        <Divider size={2} variant="interactive" />
      </div>
    ),
    renderDragPreview: (items) => (
      <div {...stylex.props(dragPreviewStyles.base)}>
        <ReorderVerticalLinesStrokeIcon />
        <Text size={16}>{items[0]['text/plain']}</Text>
        <EyeStrokeIcon />
      </div>
    ),
  });

  return (
    <>
      <DialogBar slot={DIALOG_SLOTS.TOP_BAR}>
        <Heading slot={DIALOG_SLOTS.TITLE}>Customize</Heading>
        <IconButton aria-label="Close customize dialog" slot={DIALOG_SLOTS.CLOSE} />
      </DialogBar>

      <GridList
        aria-label="Table columns"
        dragAndDropHooks={dragAndDropHooks}
        items={list.items}
        layout="grid"
        selectionMode="none"
        style={gridListStyles.base}>
        {(item) => (
          <GridListItem id={item.id} style={gridListItemStyles.base} textValue={item.label}>
            <ButtonCore aria-label={`Reorder ${item.label}`} slot="drag" {...stylex.props(gridListItemStyles.button)}>
              <ReorderVerticalLinesStrokeIcon />
            </ButtonCore>
            <Text size={16}>{item.label}</Text>

            <CheckboxCore
              aria-label={`Toggle ${item.label} visibility`}
              id={`column-toggle-${item.id}`}
              isSelected={item.visible}
              name={`column-visible-${item.id}`}
              onChange={() => list.update(item.id, { ...item, visible: !item.visible })}>
              {item.visible ? <EyeStrokeIcon style={iconStyles.base} /> : <EyeOffStrokeIcon style={iconStyles.base} />}
            </CheckboxCore>
          </GridListItem>
        )}
      </GridList>

      <DialogBar slot={DIALOG_SLOTS.BOTTOM_BAR}>
        <Button
          isRounded
          size={40}
          onPress={() => {
            setColumnsConfig(list.items.map(({ id, label, visible }) => ({ id, label, visible })));
            onClose();
          }}>
          Apply
        </Button>
      </DialogBar>
    </>
  );
}

const gridListStyles = stylex.create({
  base: {
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    scrollbarWidth: 'none',
    width: 460,
  },
});

const dropIndicatorStyles = stylex.create({
  base: {
    height: 0,
    position: 'relative',
    width: '100%',
  },
});

const dragPreviewStyles = stylex.create({
  base: {
    alignItems: 'center',
    backgroundColor: $.surfaceBGPrimary,
    borderRadius: 8,
    boxShadow: $.shadowCenter060Medium,
    display: 'flex',
    gap: 16,
    height: 40,
    opacity: 0.5,
    paddingBlock: 12,
    paddingInline: 12,
    width: 460,
  },
});

const gridListItemStyles = stylex.create({
  base: {
    alignItems: 'center',
    alignSelf: 'stretch',
    display: 'flex',
    gap: 16,
    height: 40,
    paddingBlock: 12,
    paddingInline: 12,
  },
  button: {
    backgroundColor: 'transparent',
    borderStyle: 'none',
    padding: 0,
  },
});

const iconStyles = stylex.create({
  base: {
    cursor: 'pointer',
  },
});
